/**
 * Marquee selection rule.
 *
 * LEAF NODES select on contact, matching ReactFlow's SelectionMode.Partial,
 * Figma and Miro. GROUP CONTAINERS instead need full enclosure, because a
 * group is itself a node and on contact semantics any box touching one would
 * swallow the whole group.
 *
 * Why not ReactFlow's Partial alone: its getDragItems guard for "child whose
 * parent is also selected" is broken for the modern `parentId` spelling (see
 * computeMarqueeSelection), so a group and its children both ending up
 * selected tears the group's layout apart on drag. We need the group split,
 * and ReactFlow has no hook for a custom predicate — so the canvas drops
 * ReactFlow's own `select` changes while a marquee is live and drives
 * selection from the rectangle instead.
 *
 * An earlier version of this file required the box to cover >=50% of a
 * node's area. It read well and failed in practice: these canvases hold
 * ~400-unit-tall video nodes and the natural gesture is a thin horizontal
 * band across a row, which covers ~40% — so the rule rejected every node the
 * user had visibly dragged through and the marquee selected nothing at all.
 *
 * Geometry mirrors ReactFlow's own `getOverlappingArea` (@reactflow/core
 * 11.11.4) including the `Math.ceil`; it is not exported from the package,
 * hence the copy.
 */

export interface MarqueeRect {
  x: number
  y: number
  width: number
  height: number
}

/** A node reduced to what the selection test needs. */
export interface MarqueeNodeGeometry {
  id: string
  /** Absolute position in flow coordinates (ReactFlow's `positionAbsolute`). */
  x: number
  y: number
  /** Null/undefined until ReactFlow has measured the node. */
  width: number | null | undefined
  height: number | null | undefined
}

/** ReactFlow's viewport transform: `[translateX, translateY, scale]`. */
export type FlowTransform = [number, number, number]

/**
 * Convert the marquee rectangle from screen pixels to flow coordinates.
 *
 * `userSelectionRect` in ReactFlow's store is in container pixels, while node
 * `positionAbsolute` is in flow units. Comparing the two without this step
 * silently selects the wrong nodes at any zoom other than 100%, which is the
 * failure mode this function exists to prevent.
 */
export function selectionRectToFlow(rect: MarqueeRect, transform: FlowTransform): MarqueeRect {
  const [translateX, translateY, scale] = transform
  return {
    x: (rect.x - translateX) / scale,
    y: (rect.y - translateY) / scale,
    width: rect.width / scale,
    height: rect.height / scale,
  }
}

/** Area of the intersection of two rectangles; 0 when they do not overlap. */
export function overlapArea(a: MarqueeRect, b: MarqueeRect): number {
  const xOverlap = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x))
  const yOverlap = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y))
  return Math.ceil(xOverlap * yOverlap)
}

/**
 * Does the marquee touch this node at all?
 *
 * Intersection, not a coverage fraction. An earlier version required the box
 * to cover >=50% of the node's area, which read well in the abstract and
 * failed in practice: these canvases hold ~400-unit-tall video nodes, and the
 * natural gesture is a thin horizontal band across a row. A 162-unit band
 * covers ~40% of such a node, so the rule rejected every node the user had
 * visibly dragged through and the marquee selected nothing at all. Figma,
 * Miro and ReactFlow's own SelectionMode.Partial all select on contact.
 *
 * An unmeasured node is NOT selected. ReactFlow's getNodesInside does the
 * opposite, assuming the state is transient while a node mounts, but
 * `renderOnlyVisibleElements` defaults to TRUE here so offscreen nodes carry
 * no dimensions for as long as they stay offscreen. Treating those as inside
 * put every offscreen node on the canvas into every marquee.
 */
export function nodeIntersectsMarquee(
  node: MarqueeNodeGeometry,
  flowRect: MarqueeRect,
): boolean {
  const { width, height } = node
  if (width === null || width === undefined || height === null || height === undefined) {
    return false
  }
  return overlapArea(flowRect, { x: node.x, y: node.y, width, height }) > 0
}

/** A node as the marquee sees it: geometry plus its current selection state. */
export interface MarqueeCandidate extends MarqueeNodeGeometry {
  selected: boolean
  selectable: boolean
  hidden: boolean
  /** Owning group, if this node is a group child. */
  parentId?: string | null
}

/** Walk up `parentId` and report whether any ancestor is in `ids`. */
function hasAncestorIn(
  candidate: MarqueeCandidate,
  ids: ReadonlySet<string>,
  parents: ReadonlyMap<string, string | null | undefined>,
): boolean {
  // Bounded rather than recursive: a corrupt parent cycle in YDoc would
  // otherwise hang the pointer-move handler rather than mis-select one node.
  let current = candidate.parentId
  for (let depth = 0; current && depth < 32; depth += 1) {
    if (ids.has(current)) return true
    current = parents.get(current)
  }
  return false
}

/** A ReactFlow `select` node change, reduced to what we emit. */
export interface MarqueeSelectionChange {
  id: string
  selected: boolean
}

/**
 * The ids a marquee should have selected for this rectangle.
 *
 * Returns the whole desired set rather than a change list. The caller diffs
 * it against what IT last emitted, which is the only way to avoid emitting
 * no-op `select` changes on every pointer move: ReactFlow's own
 * `nodeInternals.selected` lags a tick behind our writes, so diffing against
 * the store would either re-emit constantly or miss.
 *
 * That churn is not theoretical. A marquee drawn inside a group touches the
 * group on every move, so re-asserting per-move produced a fresh node object
 * for the container ~60x a second, and its `transition: border-color` made
 * the panel visibly flash.
 */
export function computeMarqueeSelection(
  candidates: readonly MarqueeCandidate[],
  flowRect: MarqueeRect,
): Set<string> {
  // Pass 1 — raw verdict, before group ownership is considered.
  //
  // Contact, for every node including group containers: anything the
  // rectangle touches is selected.
  //
  // Group containers used to require full enclosure, so that sweeping part
  // of a group took the children you crossed rather than the whole group.
  // That was dropped deliberately. Selecting a group already means operating
  // on all of its children -- the group carries them when it moves -- so the
  // distinction bought a special case without buying different behaviour,
  // and "whatever the box touches" is the rule that matches what people
  // expect from a marquee.
  //
  // Consequence worth knowing: brushing a group's edge now takes the group,
  // and pass 2 below then drops the individual children, so you get the
  // whole group rather than the few clips you crossed. That is the accepted
  // trade for one predictable rule.
  const parents = new Map<string, string | null | undefined>()
  for (const candidate of candidates) {
    parents.set(candidate.id, candidate.parentId)
  }

  const covered = new Set<string>()
  for (const candidate of candidates) {
    if (candidate.hidden || !candidate.selectable) continue
    if (nodeIntersectsMarquee(candidate, flowRect)) covered.add(candidate.id)
  }

  // Pass 2 — drop children whose group is itself selected.
  //
  // Dragging a group already moves its children: their `position` is
  // parent-relative, so the group's delta carries them. Selecting both makes
  // ReactFlow apply the delta to the child a SECOND time and the group's
  // internal layout tears apart mid-drag.
  //
  // ReactFlow means to prevent this -- getDragItems filters on
  // `!n.parentNode || n.parentId || !isParentSelected(...)` -- but `parentId`
  // sits in that OR chain, so for any node using the modern `parentId`
  // spelling (which is what useYCanvas writes) the clause short-circuits to
  // true and the child is dragged anyway. The guard only ever fires for the
  // deprecated `parentNode` field. Enforcing it here is the fix we own.
  const selected = new Set<string>()
  for (const candidate of candidates) {
    if (!covered.has(candidate.id)) continue
    if (hasAncestorIn(candidate, covered, parents)) continue
    selected.add(candidate.id)
  }
  return selected
}

/**
 * Ids that some other candidate claims as its parent — i.e. the group
 * containers on this canvas. Exported so diagnostics can report which nodes
 * the enclosure rule is being applied to without re-deriving it.
 */
export function collectParentIds(candidates: readonly MarqueeCandidate[]): Set<string> {
  const parentIds = new Set<string>()
  for (const candidate of candidates) {
    if (candidate.parentId) parentIds.add(candidate.parentId)
  }
  return parentIds
}

/** Minimal `select` changes to move a selection from `previous` to `next`. */
export function diffSelection(
  previous: ReadonlySet<string>,
  next: ReadonlySet<string>,
): MarqueeSelectionChange[] {
  const changes: MarqueeSelectionChange[] = []
  for (const id of next) {
    if (!previous.has(id)) changes.push({ id, selected: true })
  }
  for (const id of previous) {
    if (!next.has(id)) changes.push({ id, selected: false })
  }
  return changes
}
